/**
 * @file AppointmentInfoCard — appointment detail display.
 */
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  User,
  Calendar,
  Clock,
  MapPin,
  Video,
  FileText,
  StickyNote,
  Phone,
  Mail,
  XCircle,
  Stethoscope,
  Timer,
  Banknote,
} from "lucide-react";
import { APPOINTMENT_STRINGS } from "../constants/strings";
import { Avatar, Chip } from "../ui";
import "./AppointmentInfoCard.css";

const STATUS_CLASSES = {
  AGENDADA: "aic-banner-primary",
  CONFIRMADA: "aic-banner-ok",
  COMPLETADA: "aic-banner-neutral",
  CANCELADA: "aic-banner-err",
  NO_ASISTIO: "aic-banner-warn",
};

const STATUS_TONES = {
  AGENDADA: "primary",
  CONFIRMADA: "ok",
  COMPLETADA: "primary",
  CANCELADA: "err",
  NO_ASISTIO: "warn",
};

function DetailCell({ icon, label, children }) {
  return (
    <div className="aic-cell">
      <div className="aic-cell-head">
        {icon}
        <span className="aic-cell-label">{label}</span>
      </div>
      <div className="aic-cell-body">{children}</div>
    </div>
  );
}

export default function AppointmentInfoCard({
  appointment,
  title = "Detalle de cita",
  showProfessional = true,
  showPatient = false,
  linkPatient = true,
  showEndTime = false,
  showPatientContact = false,
  showNotes = false,
}) {
  const pro = appointment.professional;
  const proName = [pro?.title, pro?.user?.firstName, pro?.user?.lastName].filter(Boolean).join(" ");
  const proInitials = (pro?.user?.firstName?.[0] || "") + (pro?.user?.lastName?.[0] || "");
  const specialties = pro?.specialties || [];

  const location = appointment.locationId
    ? pro?.locations?.find((l) => l.id === appointment.locationId)
    : null;
  const service = appointment.serviceId
    ? pro?.consultationServices?.find((s) => s.id === appointment.serviceId)
    : null;
  const duration = pro?.sessionDuration;
  const currency = pro?.currency || "MXN";

  const dt = new Date(appointment.dateTime);
  const dateStr = dt.toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timeStr = dt.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="aic">
      <div className={`aic-banner ${STATUS_CLASSES[appointment.status] || "aic-banner-primary"}`}>
        <h2 className="aic-banner-title">{title}</h2>
        <span className="aic-banner-chip">
          {APPOINTMENT_STRINGS.STATUS_LABELS[appointment.status]}
        </span>
      </div>

      {showProfessional && (
        <div className="aic-pro">
          <Avatar size="lg" initials={proInitials.toUpperCase()} alt={proName} />
          <div className="aic-pro-meta">
            <span className="aic-pro-name">{proName}</span>
            {specialties.map((s) => (
              <span key={s.id} className="aic-pro-specialty">
                {s.name}
                {s.cedula ? ` — Céd. ${s.cedula}` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {showPatient && (
        <div className="aic-patient">
          <User size={16} />
          <div className="aic-patient-text">
            <span className="aic-patient-label">{APPOINTMENT_STRINGS.PATIENT}</span>
            {linkPatient ? (
              <RouterLink to={`/paciente/${appointment.patientId}`} className="aic-patient-link">
                {appointment.patient?.user?.firstName} {appointment.patient?.user?.lastName}
              </RouterLink>
            ) : (
              <span className="aic-patient-name">
                {appointment.patient?.user?.firstName} {appointment.patient?.user?.lastName}
              </span>
            )}
            {appointment.attendeeName && (
              <span className="aic-patient-attendee">Para: {appointment.attendeeName}</span>
            )}
          </div>
        </div>
      )}

      <div className="aic-grid">
        <DetailCell icon={<Calendar size={14} />} label="Fecha">
          <span className="aic-cell-strong aic-capitalize">{dateStr}</span>
        </DetailCell>
        <DetailCell icon={<Clock size={14} />} label="Hora">
          <span className="aic-cell-strong">{timeStr}</span>
          {showEndTime && appointment.endTime && (
            <span className="aic-cell-aside">
              {" "}
              –{" "}
              {new Date(appointment.endTime).toLocaleTimeString("es-MX", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
        </DetailCell>
        {duration && (
          <DetailCell icon={<Timer size={14} />} label="Duración">
            <span className="aic-cell-strong">{duration}</span>
            <span className="aic-cell-aside"> min</span>
          </DetailCell>
        )}
        {location && (
          <DetailCell
            icon={location.isVirtual ? <Video size={14} /> : <MapPin size={14} />}
            label="Ubicación"
          >
            <span className="aic-cell-strong">
              {location.isVirtual
                ? location.name || "Teleconsulta"
                : location.name || location.address}
            </span>
            {!location.isVirtual && (
              <span className="aic-cell-aside">
                {[
                  location.address && location.name ? location.address : null,
                  location.colonia,
                  location.city,
                  location.state,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            )}
          </DetailCell>
        )}
        {service && (
          <DetailCell icon={<Stethoscope size={14} />} label="Servicio">
            <span className="aic-cell-strong">{service.name}</span>
            {service.price != null && (
              <span className="aic-price">
                <Banknote size={12} />${Number(service.price).toLocaleString("es-MX")} {currency}
              </span>
            )}
          </DetailCell>
        )}
      </div>

      {appointment.reason && (
        <div className="aic-section">
          <div className="aic-section-head">
            <FileText size={14} />
            <span>{APPOINTMENT_STRINGS.REASON}</span>
          </div>
          <p className="aic-section-body">{appointment.reason}</p>
        </div>
      )}

      {showNotes && appointment.notes && (
        <div className="aic-section">
          <div className="aic-section-head">
            <StickyNote size={14} />
            <span>{APPOINTMENT_STRINGS.NOTES}</span>
          </div>
          <div className="aic-notes">{appointment.notes}</div>
        </div>
      )}

      {showPatientContact &&
        (appointment.patient?.user?.phone || appointment.patient?.user?.email) && (
          <div className="aic-section">
            <div className="aic-section-head">
              <Phone size={14} />
              <span>Contacto del paciente</span>
            </div>
            <div className="aic-contact-row">
              {appointment.patient.user.phone && (
                <a href={`tel:${appointment.patient.user.phone}`} className="aic-contact-chip">
                  <Phone size={12} />
                  {appointment.patient.user.phone}
                </a>
              )}
              {appointment.patient.user.email && (
                <a href={`mailto:${appointment.patient.user.email}`} className="aic-contact-chip">
                  <Mail size={12} />
                  {appointment.patient.user.email}
                </a>
              )}
            </div>
          </div>
        )}

      {(appointment.cancelledBy || appointment.cancelReason) && (
        <div className="aic-cancel">
          <div className="aic-cancel-head">
            <XCircle size={16} />
            <strong>{APPOINTMENT_STRINGS.CANCEL_REASON}</strong>
          </div>
          <p className="aic-cancel-body">
            {appointment.cancelledBy && (
              <>
                {appointment.cancelledBy === "PROFESSIONAL"
                  ? "Cancelada por el profesional"
                  : "Cancelada por el paciente"}
              </>
            )}
            {appointment.cancelReason && (
              <>
                {appointment.cancelledBy ? " — " : ""}
                {appointment.cancelReason}
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
